"use client";

import {
  createContext,
  useContext,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "@/lib/utils";

type ServiceIllustrationProps = {
  slug: string;
  className?: string;
};

const ServiceIllIdContext = createContext("");

const ink = "var(--foreground)";
const muted = "color-mix(in srgb, var(--foreground) 42%, transparent)";
const stroke = "color-mix(in srgb, var(--foreground) 12%, transparent)";
const surface = "var(--background)";
const sans = "var(--font-open-sans), system-ui, sans-serif";
const mono = "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";
const danger = "var(--brand-red)";
const ok = "#1a9d5c";

function UkCircularFlag({
  x,
  y,
  clipId,
  size = 16,
}: {
  x: number;
  y: number;
  clipId: string;
  size?: number;
}) {
  const cy = y;

  return (
    <g transform={`translate(${x}, ${cy - size / 2})`}>
      <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden>
        <defs>
          <clipPath id={clipId}>
            <circle cx="8" cy="8" r="8" />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`}>
          <rect width="16" height="16" fill="#012169" />
          <path d="M0 0 L16 16 M16 0 L0 16" stroke="#fff" strokeWidth="2.8" />
          <path
            d="M0 0 L16 16 M16 0 L0 16"
            stroke="#C8102E"
            strokeWidth="1.1"
          />
          <path d="M8 0 V16 M0 8 H16" stroke="#fff" strokeWidth="4.8" />
          <path d="M8 0 V16 M0 8 H16" stroke="#C8102E" strokeWidth="2.4" />
        </g>
        <circle
          cx="8"
          cy="8"
          r="8"
          fill="none"
          stroke={stroke}
          strokeWidth="1"
        />
      </svg>
    </g>
  );
}

function GoogleLogoIcon({
  x,
  y,
  size = 16,
}: {
  x: number;
  y: number;
  size?: number;
}) {
  const cy = y;
  const iconSize = 12;
  const inset = (size - iconSize) / 2;

  return (
    <g transform={`translate(${x}, ${cy - size / 2})`}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={size / 2}
        fill="#fff"
        stroke={stroke}
        strokeWidth="1"
      />
      <svg
        x={inset}
        y={inset}
        width={iconSize}
        height={iconSize}
        viewBox="0 0 18 18"
        aria-hidden
      >
        <g fill="none" fillRule="evenodd">
          <path
            d="M17.6 9.2l-.1-1.8H9v3.4h4.8C13.6 12 13 13 12 13.6v2.2h3a8.8 8.8 0 0 0 2.6-6.6z"
            fill="#4285F4"
            fillRule="nonzero"
          />
          <path
            d="M9 18c2.4 0 4.5-.8 6-2.2l-3-2.2a5.4 5.4 0 0 1-8-2.9H1V13a9 9 0 0 0 8 5z"
            fill="#34A853"
            fillRule="nonzero"
          />
          <path
            d="M4 10.7a5.4 5.4 0 0 1 0-3.4V5H1a9 9 0 0 0 0 8l3-2.3z"
            fill="#FBBC05"
            fillRule="nonzero"
          />
          <path
            d="M9 3.6c1.3 0 2.5.4 3.4 1.3L15 2.3A9 9 0 0 0 1 5l3 2.4a5.4 5.4 0 0 1 5-3.7z"
            fill="#EA4335"
            fillRule="nonzero"
          />
        </g>
      </svg>
    </g>
  );
}

function IllustrationShell({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 360 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("h-full w-full", className)}
      aria-hidden
    >
      <rect width="360" height="200" fill="transparent" />
      {children}
    </svg>
  );
}

function PaymentFraudIllustration() {
  const idPrefix = useContext(ServiceIllIdContext);
  const clipId = `${idPrefix}-pay-clip`;
  const scanId = `${idPrefix}-pay-scan`;
  const reduceMotion = useReducedMotion();

  return (
    <IllustrationShell>
      <defs>
        <clipPath id={clipId}>
          <rect x="70" y="32" width="220" height="136" rx="14" />
        </clipPath>
        <linearGradient id={scanId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor={ink} stopOpacity="0" />
          <stop offset="0.5" stopColor={ink} stopOpacity="0.035" />
          <stop offset="1" stopColor={ink} stopOpacity="0" />
        </linearGradient>
      </defs>

      <rect
        x="70"
        y="32"
        width="220"
        height="136"
        rx="14"
        fill={surface}
        stroke={stroke}
      />

      <text
        x="88"
        y="56"
        fill={muted}
        fontSize="10"
        fontWeight="600"
        fontFamily={sans}
      >
        Payment
      </text>
      <text x="88" y="74" fill={muted} fontSize="10" fontFamily={sans}>
        Order #4821 · Just now
      </text>

      <path
        d="M88 86h184"
        stroke={stroke}
        strokeWidth="1"
        strokeDasharray="3 4"
      />

      <text
        x="88"
        y="118"
        fill={ink}
        fontSize="22"
        fontWeight="700"
        fontFamily={sans}
        letterSpacing="-0.03em"
      >
        $313.00
      </text>
      <text x="88" y="140" fill={muted} fontSize="11" fontFamily={sans}>
        Card payment · Visa 4291
      </text>

      <g clipPath={`url(#${clipId})`}>
        <rect
          className="service-pay-scan-line"
          x="70"
          y="32"
          width="36"
          height="136"
          fill={`url(#${scanId})`}
        />
      </g>

      <motion.g
        initial={reduceMotion ? false : { scale: 0.86, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{
          type: "spring",
          stiffness: 380,
          damping: 18,
          delay: 0.12,
        }}
        style={{ transformOrigin: "252px 50px", transformBox: "fill-box" }}
      >
        <rect x="214" y="42" width="60" height="20" rx="10" fill={danger} />
        <text
          x="244"
          y="56"
          textAnchor="middle"
          fill="#fff"
          fontSize="10"
          fontWeight="700"
          fontFamily={sans}
        >
          Blocked
        </text>
      </motion.g>
    </IllustrationShell>
  );
}

function CardIssuerIllustration() {
  const reduceMotion = useReducedMotion();
  const digits = ["4", "2", "4", "2", "4", "2"];
  const results = [
    { label: "Scheme", value: "Visa" },
    { label: "Issuer", value: "Stripe" },
    { label: "Country", value: "GB" },
  ];

  return (
    <IllustrationShell>
      <text
        x="44"
        y="36"
        fill={muted}
        fontSize="10"
        fontWeight="600"
        fontFamily={sans}
      >
        BIN lookup
      </text>
      <text x="44" y="54" fill={muted} fontSize="10" fontFamily={sans}>
        First 6 digits of the card
      </text>

      {digits.map((digit, index) => (
        <motion.g
          key={`${digit}-${index}`}
          initial={reduceMotion ? false : { y: 10, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{
            duration: 0.3,
            delay: index * 0.05,
            ease: [0.23, 1, 0.32, 1],
          }}
        >
          <rect
            x={44 + index * 32}
            y="66"
            width="28"
            height="36"
            rx="7"
            fill={surface}
            stroke={stroke}
          />
          <text
            x={58 + index * 32}
            y="90"
            textAnchor="middle"
            fill={ink}
            fontSize="16"
            fontWeight="700"
            fontFamily={mono}
          >
            {digit}
          </text>
        </motion.g>
      ))}

      {[0, 1, 2, 3].map((dot) => (
        <circle
          key={`a-${dot}`}
          cx={248 + dot * 8}
          cy="84"
          r="2.2"
          fill={muted}
          opacity="0.45"
        />
      ))}
      {[0, 1, 2, 3].map((dot) => (
        <circle
          key={`b-${dot}`}
          cx={288 + dot * 8}
          cy="84"
          r="2.2"
          fill={muted}
          opacity="0.45"
        />
      ))}

      <path d="M44 118h272" stroke={stroke} strokeWidth="1" />

      {results.map((result, index) => (
        <motion.g
          key={result.label}
          initial={reduceMotion ? false : { y: 8, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{
            duration: 0.35,
            delay: 0.28 + index * 0.08,
            ease: [0.23, 1, 0.32, 1],
          }}
        >
          <text
            x={44 + index * 104}
            y="142"
            fill={muted}
            fontSize="10"
            fontFamily={sans}
          >
            {result.label}
          </text>
          <text
            x={44 + index * 104}
            y="164"
            fill={ink}
            fontSize="14"
            fontWeight="700"
            fontFamily={sans}
          >
            {result.value}
          </text>
        </motion.g>
      ))}
    </IllustrationShell>
  );
}

function IbanBranchIcon({
  type,
  x,
  y,
  clipId,
}: {
  type: "country" | "bank" | "check";
  x: number;
  y: number;
  clipId?: string;
}) {
  const size = 16;
  const cx = x + size / 2;
  const cy = y;

  if (type === "country") {
    return (
      <UkCircularFlag x={x} y={y} clipId={clipId ?? `${x}-${y}-uk-flag`} />
    );
  }

  if (type === "bank") {
    return (
      <g transform={`translate(${x}, ${cy - size / 2})`}>
        <path
          d="M8 2.5 2.5 6.2V7.2h11V6.2L8 2.5Z"
          fill="color-mix(in srgb, var(--foreground) 10%, transparent)"
          stroke={ink}
          strokeWidth="1"
          strokeLinejoin="round"
        />
        <rect
          x="3.5"
          y="7.5"
          width="9"
          height="6"
          rx="1"
          fill={surface}
          stroke={ink}
          strokeWidth="1"
        />
        <path
          d="M5.5 7.5v6M8 7.5v6M10.5 7.5v6"
          stroke={ink}
          strokeWidth="0.9"
          strokeLinecap="round"
        />
      </g>
    );
  }

  return (
    <g>
      <circle cx={cx} cy={cy} r={size / 2} fill={ok} />
      <path
        d={`M${cx - 3.2} ${cy + 0.2} ${cx - 0.8} ${cy + 2.6} ${cx + 3.4} ${cy - 2.2}`}
        fill="none"
        stroke="#fff"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
}

function IbanIllustration() {
  const reduceMotion = useReducedMotion();
  const idPrefix = useContext(ServiceIllIdContext);
  const flagClipId = `${idPrefix}-iban-flag`;
  const trunkX = 72;
  const branches = [
    { y: 98, type: "country" as const, value: "GB · United Kingdom" },
    { y: 134, type: "bank" as const, value: "NWBK · NatWest" },
    { y: 170, type: "check" as const, value: "Valid IBAN" },
  ];
  const iconX = trunkX + 36;
  const textX = iconX + 22;

  return (
    <IllustrationShell>
      <rect
        x="40"
        y="16"
        width="280"
        height="48"
        rx="12"
        fill={surface}
        stroke={stroke}
      />
      <text
        x="56"
        y="34"
        fill={muted}
        fontSize="9"
        fontWeight="600"
        fontFamily={sans}
      >
        IBAN
      </text>
      <text
        x="56"
        y="52"
        fill={ink}
        fontSize="12"
        fontWeight="600"
        fontFamily={mono}
        letterSpacing="0.02em"
      >
        GB29 NWBK 6016 1331 9268 19
      </text>

      <path
        className="service-trace-line"
        d={`M${trunkX} 64V${branches[branches.length - 1].y}`}
        pathLength={1}
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
      />

      {branches.map((branch, index) => (
        <g key={branch.type}>
          <path
            className="service-trace-line"
            d={`M${trunkX} ${branch.y}h24`}
            pathLength={1}
            stroke={stroke}
            strokeWidth="1.5"
            strokeLinecap="round"
            style={{ animationDelay: `${0.16 + index * 0.1}s` }}
          />
          <motion.g
            initial={reduceMotion ? false : { x: -6, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{
              duration: 0.35,
              delay: 0.22 + index * 0.1,
              ease: [0.23, 1, 0.32, 1],
            }}
          >
            <IbanBranchIcon
              type={branch.type}
              x={iconX}
              y={branch.y}
              clipId={branch.type === "country" ? flagClipId : undefined}
            />
            <text
              x={textX}
              y={branch.y + 4}
              fill={branch.type === "check" ? ok : muted}
              fontSize="12"
              fontWeight="500"
              fontFamily={sans}
            >
              {branch.value}
            </text>
          </motion.g>
        </g>
      ))}
    </IllustrationShell>
  );
}

const CONTENT_MODERATION_OFFSET_Y = 48;

function ContentModerationIllustration() {
  const reduceMotion = useReducedMotion();
  const crapRef = useRef<SVGTSpanElement>(null);
  const [crapBox, setCrapBox] = useState<{ x: number; width: number } | null>(
    null,
  );

  const labelSize = 13;
  const bodySize = 24;
  const labelY = 16;
  const line1Y = 48;
  const line2Y = 86;
  const squiggleY = line1Y + 8;

  useLayoutEffect(() => {
    const measure = () => {
      const el = crapRef.current;
      if (!el) return;
      const { x, width } = el.getBBox();
      setCrapBox({ x, width });
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const squiggleAmp = 3;
  const squiggleSegments = 4;
  const squiggleWidth = crapBox?.width ?? 42;
  const squiggleX = crapBox?.x ?? 158;
  const squiggleSegW = squiggleWidth / squiggleSegments;
  const squigglePath = Array.from({ length: squiggleSegments }, (_, index) => {
    const wave = index % 2 === 0 ? squiggleAmp : -squiggleAmp;
    return `q${squiggleSegW / 2} ${wave} ${squiggleSegW} 0`;
  }).join(" ");
  const squiggleD = `M${squiggleX} ${squiggleY} ${squigglePath}`;

  return (
    <IllustrationShell>
      <g transform={`translate(44, ${CONTENT_MODERATION_OFFSET_Y})`}>
        <text
          y={labelY}
          fill={muted}
          fontSize={labelSize}
          fontWeight="600"
          fontFamily={sans}
        >
          Profane text
        </text>

        <text y={line1Y} fontSize={bodySize} fontFamily={sans}>
          <tspan fill={ink} fontWeight="500">This product is </tspan>
          <tspan ref={crapRef} fill={danger} fontWeight="700">crap</tspan>
          <tspan fill={ink} fontWeight="500">.</tspan>
        </text>

        {crapBox ? (
          <path
            className="service-trace-line"
            d={squiggleD}
            pathLength={1}
            fill="none"
            stroke={danger}
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        ) : null}

        <motion.g
          initial={reduceMotion ? false : { y: 4, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.35, delay: 0.18, ease: [0.23, 1, 0.32, 1] }}
        >
          <text y={line2Y} fontSize={bodySize} fontFamily={sans}>
            <tspan fill={muted} fontWeight="500">
              This product is{" "}
            </tspan>
            <tspan fill={muted} fontWeight="600" fontFamily={mono}>
              ****
            </tspan>
            <tspan fill={muted} fontWeight="500">
              .
            </tspan>
          </text>
        </motion.g>
      </g>
    </IllustrationShell>
  );
}

const alert = "var(--brand-orange)";

function ProxyBranchIcon({
  type,
  x,
  y,
  clipId,
}: {
  type: "type" | "country" | "blocked";
  x: number;
  y: number;
  clipId?: string;
}) {
  const size = 16;
  const cx = x + size / 2;
  const cy = y;

  if (type === "type") {
    return (
      <g>
        <circle cx={cx} cy={cy} r={size / 2} fill={alert} />
        <path
          d={`M${cx} ${cy - 3.5}V${cy + 1}`}
          stroke="#fff"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy + 3.3} r="0.85" fill="#fff" />
      </g>
    );
  }

  if (type === "country") {
    return (
      <g transform={`translate(${x}, ${cy - size / 2})`}>
        <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden>
          <defs>
            <clipPath id={clipId}>
              <circle cx="8" cy="8" r="8" />
            </clipPath>
          </defs>
          <g clipPath={`url(#${clipId})`}>
            <rect width="16" height="5.34" fill="#AE1C28" />
            <rect y="5.33" width="16" height="5.34" fill="#FFFFFF" />
            <rect y="10.67" width="16" height="5.33" fill="#21468B" />
          </g>
          <circle
            cx="8"
            cy="8"
            r="8"
            fill="none"
            stroke={stroke}
            strokeWidth="1"
          />
        </svg>
      </g>
    );
  }

  return (
    <g>
      <circle cx={cx} cy={cy} r={size / 2} fill={danger} />
      <path
        d={`M${cx - 3.2} ${cy - 3.2} ${cx + 3.2} ${cy + 3.2} M${cx + 3.2} ${cy - 3.2} ${cx - 3.2} ${cy + 3.2}`}
        fill="none"
        stroke="#fff"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </g>
  );
}

function ProxyVpnIllustration() {
  const reduceMotion = useReducedMotion();
  const idPrefix = useContext(ServiceIllIdContext);
  const flagClipId = `${idPrefix}-proxy-nl-flag`;
  const trunkX = 72;
  const branches = [
    { y: 98, type: "type" as const, value: "VPN" },
    { y: 134, type: "country" as const, value: "Netherlands" },
    { y: 170, type: "blocked" as const, value: "Blocked", alert: true },
  ];
  const iconX = trunkX + 36;
  const textX = iconX + 22;

  return (
    <IllustrationShell>
      <rect
        x="40"
        y="16"
        width="280"
        height="48"
        rx="12"
        fill={surface}
        stroke={stroke}
      />
      <text
        x="56"
        y="34"
        fill={muted}
        fontSize="9"
        fontWeight="600"
        fontFamily={sans}
      >
        Incoming IP
      </text>
      <text
        x="56"
        y="52"
        fill={ink}
        fontSize="14"
        fontWeight="700"
        fontFamily={mono}
        letterSpacing="-0.02em"
      >
        185.220.101.4
      </text>
      <rect x="236" y="26" width="68" height="20" rx="10" fill={danger} />
      <text
        x="270"
        y="40"
        textAnchor="middle"
        fill="#fff"
        fontSize="10"
        fontWeight="700"
        fontFamily={sans}
      >
        VPN
      </text>

      <path
        className="service-trace-line"
        d={`M${trunkX} 64V${branches[branches.length - 1].y}`}
        pathLength={1}
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
      />

      {branches.map((branch, index) => (
        <g key={branch.type}>
          <path
            className="service-trace-line"
            d={`M${trunkX} ${branch.y}h24`}
            pathLength={1}
            stroke={stroke}
            strokeWidth="1.5"
            strokeLinecap="round"
            style={{ animationDelay: `${0.16 + index * 0.1}s` }}
          />
          <motion.g
            initial={reduceMotion ? false : { x: -6, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{
              duration: 0.35,
              delay: 0.22 + index * 0.1,
              ease: [0.23, 1, 0.32, 1],
            }}
          >
            <ProxyBranchIcon
              type={branch.type}
              x={iconX}
              y={branch.y}
              clipId={branch.type === "country" ? flagClipId : undefined}
            />
            <text
              x={textX}
              y={branch.y + 4}
              fill={branch.alert ? danger : muted}
              fontSize="12"
              fontWeight={branch.alert ? 700 : 500}
              fontFamily={sans}
            >
              {branch.value}
            </text>
          </motion.g>
        </g>
      ))}
    </IllustrationShell>
  );
}

function IpLocationIllustration() {
  const reduceMotion = useReducedMotion();
  const results = [
    { label: "City", value: "Westminster" },
    { label: "Country", value: "UK" },
    { label: "Time", value: "GMT" },
  ];

  return (
    <IllustrationShell>
      <text
        x="44"
        y="28"
        fill={muted}
        fontSize="10"
        fontWeight="600"
        fontFamily={sans}
      >
        IP lookup
      </text>
      <text
        x="44"
        y="50"
        fill={ink}
        fontSize="13"
        fontWeight="600"
        fontFamily={mono}
        letterSpacing="-0.02em"
      >
        81.2.69.142
      </text>

      <text
        x="44"
        y="92"
        fill={ink}
        fontSize="22"
        fontWeight="700"
        fontFamily={mono}
        letterSpacing="-0.04em"
      >
        51.50°N
      </text>

      <motion.g
        initial={reduceMotion ? false : { y: -10, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{
          type: "spring",
          stiffness: 380,
          damping: 18,
          delay: 0.12,
        }}
      >
        <path
          d="M180 70c-5 0-9 4-9 9 0 6.6 9 13 9 13s9-6.4 9-13c0-5-4-9-9-9z"
          fill={danger}
        />
        <circle cx="180" cy="78" r="2.6" fill="#fff" />
      </motion.g>

      <text
        x="316"
        y="92"
        textAnchor="end"
        fill={ink}
        fontSize="22"
        fontWeight="700"
        fontFamily={mono}
        letterSpacing="-0.04em"
      >
        0.14°W
      </text>

      <path d="M44 118h272" stroke={stroke} strokeWidth="1" />

      {results.map((result, index) => (
        <motion.g
          key={result.label}
          initial={reduceMotion ? false : { y: 8, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{
            duration: 0.35,
            delay: 0.24 + index * 0.08,
            ease: [0.23, 1, 0.32, 1],
          }}
        >
          <text
            x={44 + index * 104}
            y="142"
            fill={muted}
            fontSize="10"
            fontFamily={sans}
          >
            {result.label}
          </text>
          <text
            x={44 + index * 104}
            y="164"
            fill={ink}
            fontSize="14"
            fontWeight="700"
            fontFamily={sans}
          >
            {result.value}
          </text>
        </motion.g>
      ))}
    </IllustrationShell>
  );
}

function AsnIllustration() {
  const reduceMotion = useReducedMotion();
  const idPrefix = useContext(ServiceIllIdContext);
  const ukFlagClipId = `${idPrefix}-asn-uk-flag`;
  const trunkX = 72;
  const labelX = trunkX + 36;
  const branches = [
    { y: 98, label: "Owner", value: "Google LLC", icon: "logo" as const },
    {
      y: 134,
      label: "Country",
      value: "United Kingdom",
      icon: "flag" as const,
    },
    { y: 170, label: "Type", value: "Cloud", icon: null },
  ];

  return (
    <IllustrationShell>
      <rect
        x="40"
        y="16"
        width="280"
        height="48"
        rx="12"
        fill={surface}
        stroke={stroke}
      />
      <text
        x="56"
        y="34"
        fill={muted}
        fontSize="9"
        fontWeight="600"
        fontFamily={sans}
      >
        ASN
      </text>
      <text
        x="56"
        y="52"
        fill={ink}
        fontSize="16"
        fontWeight="700"
        fontFamily={mono}
        letterSpacing="-0.02em"
      >
        AS15169
      </text>

      <path
        className="service-trace-line"
        d={`M${trunkX} 64V${branches[branches.length - 1].y}`}
        pathLength={1}
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
      />

      {branches.map((branch, index) => {
        const labelWidth =
          branch.label === "Owner" ? 46 : branch.label === "Country" ? 52 : 38;
        const valueX = labelX + labelWidth;

        return (
          <g key={branch.label}>
            <path
              className="service-trace-line"
              d={`M${trunkX} ${branch.y}h24`}
              pathLength={1}
              stroke={stroke}
              strokeWidth="1.5"
              strokeLinecap="round"
              style={{ animationDelay: `${0.16 + index * 0.1}s` }}
            />
            <motion.g
              initial={reduceMotion ? false : { x: -6, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{
                duration: 0.35,
                delay: 0.22 + index * 0.1,
                ease: [0.23, 1, 0.32, 1],
              }}
            >
              <text
                x={labelX}
                y={branch.y + 4}
                fill={ink}
                fontSize="12"
                fontWeight="600"
                fontFamily={sans}
              >
                {branch.label}
              </text>
              {branch.icon === "logo" ? (
                <>
                  <GoogleLogoIcon x={valueX} y={branch.y} />
                  <text
                    x={valueX + 22}
                    y={branch.y + 4}
                    fill={muted}
                    fontSize="12"
                    fontWeight="500"
                    fontFamily={sans}
                  >
                    {branch.value}
                  </text>
                </>
              ) : branch.icon === "flag" ? (
                <>
                  <UkCircularFlag
                    x={valueX}
                    y={branch.y}
                    clipId={ukFlagClipId}
                    size={14}
                  />
                  <text
                    x={valueX + 20}
                    y={branch.y + 4}
                    fill={muted}
                    fontSize="12"
                    fontWeight="500"
                    fontFamily={sans}
                  >
                    {branch.value}
                  </text>
                </>
              ) : (
                <text
                  x={valueX}
                  y={branch.y + 4}
                  fill={muted}
                  fontSize="12"
                  fontWeight="500"
                  fontFamily={sans}
                >
                  {branch.value}
                </text>
              )}
            </motion.g>
          </g>
        );
      })}
    </IllustrationShell>
  );
}

function CountryIllustration() {
  const reduceMotion = useReducedMotion();
  const neighbors = [
    { code: "DK", x: 180, y: 40 },
    { code: "NL", x: 78, y: 78 },
    { code: "PL", x: 282, y: 78 },
    { code: "FR", x: 88, y: 168 },
    { code: "AT", x: 180, y: 184 },
    { code: "CZ", x: 272, y: 168 },
  ];

  return (
    <IllustrationShell>
      <text
        x="44"
        y="24"
        fill={muted}
        fontSize="10"
        fontWeight="600"
        fontFamily={sans}
      >
        Neighbours
      </text>

      {neighbors.map((place, index) => (
        <motion.text
          key={place.code}
          x={place.x}
          y={place.y}
          textAnchor="middle"
          fill={muted}
          fontSize="12"
          fontWeight="600"
          fontFamily={mono}
          letterSpacing="0.04em"
          initial={reduceMotion ? false : { opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{
            duration: 0.35,
            delay: 0.12 + index * 0.05,
            ease: [0.23, 1, 0.32, 1],
          }}
        >
          {place.code}
        </motion.text>
      ))}

      <motion.g
        initial={reduceMotion ? false : { scale: 0.94, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.4, delay: 0.08, ease: [0.23, 1, 0.32, 1] }}
        style={{ transformOrigin: "180px 112px", transformBox: "fill-box" }}
      >
        <text
          x="180"
          y="108"
          textAnchor="middle"
          fill={ink}
          fontSize="36"
          fontWeight="700"
          fontFamily={mono}
          letterSpacing="0.06em"
        >
          DE
        </text>
        <text
          x="180"
          y="144"
          textAnchor="middle"
          fill={ink}
          fontSize="13"
          fontWeight="600"
          fontFamily={sans}
        >
          Germany
        </text>
      </motion.g>
    </IllustrationShell>
  );
}

function UserDataIllustration() {
  const reduceMotion = useReducedMotion();
  const scores = [0, 1, 2, 3];
  const scoreLabels = ["Safe", "Low", "Medium", "High"];
  const active = 0;
  const track = [60, 128, 196, 264];
  const results = [
    { label: "Valid", value: "Yes" },
    { label: "Disposable", value: "No" },
    { label: "Free", value: "No" },
  ];

  return (
    <IllustrationShell>
      <text
        x="44"
        y="28"
        fill={muted}
        fontSize="10"
        fontWeight="600"
        fontFamily={sans}
      >
        Email scoring
      </text>
      <text x="44" y="52" fontSize="18" fontWeight="600" fontFamily={mono}>
        <tspan fill={ink}>alex</tspan>
        <tspan fill={muted}>@</tspan>
        <tspan fill={ink}>company.com</tspan>
      </text>

      <path
        d={`M${track[0]} 82H${track[track.length - 1]}`}
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
      />

      {scores.map((score, index) => {
        const isActive = score === active;
        return (
          <motion.g
            key={score}
            initial={reduceMotion ? false : { scale: 0.86, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{
              duration: 0.3,
              delay: 0.1 + index * 0.06,
              ease: [0.23, 1, 0.32, 1],
            }}
            style={{
              transformOrigin: `${track[index]}px 82px`,
              transformBox: "fill-box",
            }}
          >
            <circle
              cx={track[index]}
              cy="82"
              r={isActive ? 7 : 4.5}
              fill={isActive ? ok : surface}
              stroke={isActive ? ok : stroke}
              strokeWidth="1.5"
            />
            <text
              x={track[index]}
              y="104"
              textAnchor="middle"
              fill={isActive ? ok : muted}
              fontSize="10"
              fontWeight={isActive ? 700 : 500}
              fontFamily={sans}
            >
              {scoreLabels[score]}
            </text>
          </motion.g>
        );
      })}

      <path d="M44 118h272" stroke={stroke} strokeWidth="1" />

      {results.map((result, index) => (
        <motion.g
          key={result.label}
          initial={reduceMotion ? false : { y: 8, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{
            duration: 0.35,
            delay: 0.28 + index * 0.08,
            ease: [0.23, 1, 0.32, 1],
          }}
        >
          <text
            x={44 + index * 104}
            y="142"
            fill={muted}
            fontSize="10"
            fontFamily={sans}
          >
            {result.label}
          </text>
          <text
            x={44 + index * 104}
            y="164"
            fill={ink}
            fontSize="14"
            fontWeight="700"
            fontFamily={sans}
          >
            {result.value}
          </text>
        </motion.g>
      ))}
    </IllustrationShell>
  );
}

const illustrations: Record<string, () => React.JSX.Element> = {
  "payment-fraud-analysis": PaymentFraudIllustration,
  "card-issuer-verification": CardIssuerIllustration,
  "iban-validation-and-insights": IbanIllustration,
  "content-moderation": ContentModerationIllustration,
  "vpn-proxy-detection": ProxyVpnIllustration,
  "ip-location-intellegence": IpLocationIllustration,
  "network-intellegence-asn": AsnIllustration,
  "country-intellegence": CountryIllustration,
  "data-scoring-and-validation": UserDataIllustration,
};

export default function ServiceIllustration({
  slug,
  className,
}: ServiceIllustrationProps) {
  const idPrefix = useId().replace(/:/g, "");
  const Illustration = illustrations[slug] ?? PaymentFraudIllustration;

  return (
    <ServiceIllIdContext.Provider value={idPrefix}>
      <div
        className={cn(
          "service-illustration relative cursor-default overflow-hidden select-none",
          className,
        )}
      >
        <Illustration />
      </div>
    </ServiceIllIdContext.Provider>
  );
}
